import React, { useState, useEffect } from 'react';
import {
  ResourceList,
  ResourceItem,
  Stack,
  ButtonGroup,
  Button,
  TextStyle,
  Icon,
  Badge,
  Tooltip,
  Text
} from '@shopify/polaris';
import './TemplateList.css'; // Import custom CSS for styling overrides
import { DeleteMajor, EditMajor, DuplicateMinor, PlayMajor, DragHandleMinor } from '@shopify/polaris-icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { calculateTemplateCredits, getDetailedCreditCalculation } from '../../utils/templateCreditCalculator';

/**
 * A component for displaying and managing a list of templates
 * 
 * @param {Object} props
 * @param {Array} props.templates - Array of template objects to display
 * @param {Array} props.selectedTemplates - Array of selected template IDs
 * @param {Function} props.handleTemplateSelect - Callback when a template is selected
 * @param {Function} props.handleDelete - Callback when delete button is clicked
 * @param {Function} props.handleEdit - Callback when edit button is clicked
 * @param {Function} props.handleDuplicate - Callback when duplicate button is clicked
 * @param {Function} props.handleExecute - Callback when execute button is clicked
 * @param {Function} props.onReorder - Callback when templates are reordered
 * @param {Array} props.aiModels - Array of available AI models
 */
export const TemplateList = ({
  templates = [],
  selectedTemplates = [],
  handleTemplateSelect,
  handleDelete,
  handleEdit,
  handleDuplicate,
  handleExecute,
  onReorder,
  aiModels = [] // New prop for available AI models
}) => {
  // Helper function to get model display name
  const getModelDisplayName = (modelId) => {
    if (!modelId || !aiModels || aiModels.length === 0) {
      // Find the default model if no specific model is selected
      const defaultModel = aiModels?.find(m => m.isDefault);
      return defaultModel ? defaultModel.displayName : 'Unknown';
    }
    
    const model = aiModels.find(m => m.id === modelId);
    if (!model) return 'Unknown';
    
    // Add '(Vision)' suffix for vision-capable models
    return model.type === 'vision' ? `${model.displayName} (V)` : model.displayName;
  };

  // Helper function to get model cost per output field
  const getModelCostPerOutput = (modelId) => {
    if (!modelId || !aiModels || aiModels.length === 0) return 1;
    const model = aiModels.find(m => m.id === modelId);
    
    return model ? model.creditCostPerOutput || 1 : 1;
  };

  // Helper function to calculate total credits with smart field-based pricing
  const calculateTotalCredits = (template, modelId) => {
    // For tool templates, always return 0 credits
    if (template.type === 'tool') {
      return 0;
    }
    
    const aiModelCost = getModelCostPerOutput(modelId);
    
    // Use smart credit calculation
    return calculateTemplateCredits(template, aiModelCost);
  };

  // Internal state to maintain visual stability during drag operations
  const [internalItems, setInternalItems] = useState(templates);
  
  // Update internal items when external templates change
  useEffect(() => {
    setInternalItems(templates);
  }, [templates]);
  
  // Simple styles for dragging - minimal to let react-beautiful-dnd's defaults work
  const getItemStyle = (isDragging, draggableStyle) => ({
    // Apply a subtle highlight when dragging
    backgroundColor: isDragging ? '#f9fafb' : undefined,
    // This is important - we must pass on the draggableStyle
    ...draggableStyle
  });
  
  const getListStyle = isDraggingOver => ({
    // Subtle background change when dragging over the list
    backgroundColor: isDraggingOver ? '#f4f6f8' : undefined,
    padding: '4px',
    width: '100%'
  });

  // Handle drag end event according to react-beautiful-dnd documentation
  // Handle drag end event from react-beautiful-dnd
  const handleDragEnd = (result) => {
    console.log('Drag end result:', result);
    
    // Don't continue if dropped outside the list
    if (!result.destination) {
      console.log('Dropped outside the list');
      return;
    }
    
    const { source, destination } = result;
    
    // No movement
    if (source.index === destination.index) {
      console.log('No position change, skipping reorder');
      return;
    }
    
    console.log(`Moving from index ${source.index} to index ${destination.index}`);
    
    // Create a new array and perform the reorder
    const newItems = [...internalItems];
    const [movedItem] = newItems.splice(source.index, 1);
    newItems.splice(destination.index, 0, movedItem);
    
    console.log('New order:', newItems.map((t, i) => `${i}: ${t.name}`));
    
    // Update internal state for immediate UI update
    setInternalItems(newItems);
    
    // Pass the fully reordered array to the parent
    if (onReorder) {
      onReorder(newItems);
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="template-list">
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={getListStyle(snapshot.isDraggingOver)}
            className="template-list"
          >
            {internalItems.map((template, index) => {
              const { id, name, type = 'template', modelId, outputFields = [], outputMetafields = [] } = template;
              const isSelected = selectedTemplates.includes(id);
              const selectionIndex = selectedTemplates.indexOf(id);
              
              return (
                <Draggable key={id} draggableId={id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...getItemStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style
                        ),
                        padding: '4px',
                        width: '100%',
                        ...provided.draggableProps.style
                      }}
                      className={`template-list-item ${snapshot.isDragging ? 'dragging' : ''}`}
                    >
                      <ResourceItem
                        id={id}
                        onClick={() => handleTemplateSelect && handleTemplateSelect(id)}
                        selected={isSelected}
                        accessibilityLabel={`Select ${name}`}
                        className="clean-resource-item" // Add custom class for styling
                        media={<span style={{ display: 'none' }}></span>} // Override media space to hide bullet
                      >
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', minHeight: '60px' }}>
                          {/* Drag Handle */}
                          <div {...provided.dragHandleProps} style={{ 
                            cursor: 'grab', 
                            padding: '8px 4px', 
                            marginRight: '8px',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <Icon source={DragHandleMinor} color="subdued" />
                          </div>
                          
                          {/* Selection Badge */}
                          <div style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                            {isSelected && (
                              <Badge status="success">{selectionIndex + 1}</Badge>
                            )}
                          </div>
                          
                          {/* Template Info - Takes up remaining space */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ marginBottom: '4px' }}>
                              <TextStyle variation={isSelected ? 'strong' : undefined}>
                                {name || 'Unnamed Template'}
                              </TextStyle>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                              <TextStyle variation="subdued">
                                Type: {
                                  type === 'product' ? 'Product' : 
                                  type === 'variant' ? 'Variant' : 
                                  type === 'image' ? 'Image' : 
                                  type === 'tool' ? 'Tool Execution' : 
                                  'Unknown'
                                }
                              </TextStyle>
                              {type !== 'tool' && (
                                <TextStyle variation="subdued">
                                  Model: {getModelDisplayName(modelId)}
                                </TextStyle>
                              )}
                              {type === 'tool' ? (
                                <Badge status="success">No AI Credits</Badge>
                              ) : (() => {
                                const aiModelCost = getModelCostPerOutput(modelId);
                                const totalCredits = calculateTotalCredits(template, modelId);
                                const details = getDetailedCreditCalculation(template, aiModelCost);
                                
                                return (
                                  <Tooltip content={details.discount > 0 ? `Smart pricing: ${details.discount}% discount applied` : `AI Model Cost: ${aiModelCost}`}>
                                    <Badge status={details.discount > 0 ? "success" : "info"}>
                                      Credits: {totalCredits} {details.discount > 0 ? `(-${details.discount}%)` : ''}
                                    </Badge>
                                  </Tooltip>
                                );
                              })()}
                            </div>
                          </div>
                          
                          {/* Action Buttons - Fixed width and alignment */}
                          {(handleExecute || handleEdit || handleDuplicate || handleDelete) && (
                            <div style={{ 
                              marginLeft: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              flexShrink: 0
                            }}>
                              <ButtonGroup segmented>
                                {handleExecute && (
                                  <Button 
                                    icon={<Icon source={PlayMajor} color="success" />} 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleExecute(id);
                                    }}
                                    accessibilityLabel={`Execute ${name}`}
                                    size="slim"
                                  />
                                )}
                                {handleEdit && (
                                  <Button 
                                    icon={<Icon source={EditMajor} color="base" />} 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(template);
                                    }}
                                    accessibilityLabel={`Edit ${name}`}
                                    size="slim"
                                  />
                                )}
                                {handleDuplicate && (
                                  <Button 
                                    icon={<Icon source={DuplicateMinor} color="base" />} 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDuplicate(template);
                                    }}
                                    accessibilityLabel={`Duplicate ${name}`}
                                    size="slim"
                                  />
                                )}
                                {handleDelete && (
                                  <Button 
                                    icon={<Icon source={DeleteMajor} color="critical" />} 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(template.id);
                                    }}
                                    accessibilityLabel={`Delete ${name}`}
                                    destructive
                                    size="slim"
                                  />
                                )}
                              </ButtonGroup>
                            </div>
                          )}
                        </div>
                      </ResourceItem>
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default TemplateList;
